/**
 * Impostazioni sul telefono (7/10/2026): risponde a "chi entra nel
 * gestionale, e i servizi collegati funzionano?". Sono le impostazioni
 * dell'iPhone: in cima l'organizzazione, sotto un elenco di voci, e ogni voce
 * apre la sua pagina (?voce=…, così la freccia indietro del telefono torna
 * all'elenco).
 *
 * Prima, a 390px: il menu a due colonne del computer dentro una scheda, poi
 * "Filtri" da solo su una riga, due avvisi "si fa dal computer" e gli utenti
 * come schede RUOLO / STATO. Le integrazioni erano tre riquadri di numeri e
 * la matrice dei permessi non c'era.
 * Qui:
 *   1. l'organizzazione, con quante persone e se i collegamenti vanno;
 *   2. Persone e accessi: gli utenti (ognuno con la sua pagina) e i ruoli,
 *      quattro, ognuno detto in una frase (RuoliSemplici, come sul computer:
 *      la tabella dei permessi per sezione è uscita il 28/10/2026);
 *   3. Servizi collegati: FattureInCloud, Qonto e gli altri, col loro stato;
 *      dal 28/10/2026, solo per gli Amministratori, anche l'Assistente: stato,
 *      interruttori, spesa del mese e «Cosa ha fatto MUNE» con l'Annulla
 *      (os-planning/ai/fase-1-progetto.md, decisione 15);
 *   4. Organizzazione: le preferenze.
 * Si modifica come sull'iPhone: la riga mostra il valore, si tocca, sale un
 * elenco di scelte (MScelte) o un campo solo con Salva (MCampo). Mai un
 * modulo di campi in fila. Restano al computer le cose nuove (utente,
 * collegamento) e collegare o scollegare un servizio: servono chiavi da
 * incollare. Stesse chiamate e stesse chiavi di cache del computer.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { confirm as confirmDialog } from '../../stores/confirm.store';
import { useAuthStore } from '../../stores/auth.store';
import { useModalStore } from '../../stores/modal.store';
import { useUrlState } from '../../hooks/useUrlState';
import { Icon, type IconName } from '../../components/ui/Icon';
import { MobileSheet } from '../../components/ui/MobileSheet';
import { DesktopOnly } from '../../components/ui/DesktopOnly';
import { MCampo, MEmpty, MGroup, MHead, MPage, MRow, MScelte, MSearch, iniziali } from '../../components/mobile/MobileKit';
import { ModuleKey, PermissionLevel, hasAtLeast } from '@masamune/shared';
import { RuoliSemplici } from './RuoliSemplici';
import { consumiQuery, dollari, nomeModello, salvaImpostazioni, statoAssistenteQuery, type StatoAssistente } from '../assistente/assistenteApi';
import { chiamate, dollariCorti, meseDiAdesso, meseInParole, percentoSpeso } from '../assistente/registroApi';
import { AzioniAssistenteMobile } from '../assistente/AzioniAssistente';
import { PostaGoogleMobile } from './PostaGoogle';
import { SPIEGA_RUOLO, ruoliAssegnabili, ruoliVisibili, ruoloVecchio } from '../../lib/ruoli';
import './settings-mobile.css';

interface Role {
  id: string; key: string | null; name: string; color: string; shortLabel: string;
  canSeeMargin: boolean; isSystem: boolean; usersCount: number;
  permissions: Array<{ module: string; level: string }>;
}
interface UserItem {
  id: string; email: string; firstName: string; lastName: string;
  initials: string; avatarColor: string; active: boolean;
  role: { id: string; name: string; color: string; shortLabel: string; key: string | null };
}
interface OrgSettings {
  organizationName: string; logoUrl: string | null; primaryColor: string;
  marginThreshold: string; currency: string; timezone: string;
  dateFormat: string; docLanguage: string; fiscalYearStart: number;
}
interface Integration {
  id: string; kind: string; name: string; status: string;
  config: any; lastSyncAt: string | null; hasKey: boolean;
  connectedBy: { firstName: string; lastName: string } | null;
}
interface FicStatus { connected: boolean; companyId: number | null; companyName: string | null; connectedAt: string | null; lastSyncAt: string | null }
interface QontoStatus {
  connected: boolean; organizationName?: string | null; orgSlug?: string | null; login?: string | null;
  connectedAt?: string | null; lastSyncAt?: string | null; accounts?: Array<{ id: string; name: string; balance: number }> | null;
}

// Le stesse chiavi e opzioni della pagina del computer: una cache sola.
const ficStatusQuery = { queryKey: ['fic-status'] as const, queryFn: () => api.get<FicStatus>('/settings/integrations/fic/status'), refetchOnWindowFocus: false };
const qontoStatusQuery = { queryKey: ['qonto-status'] as const, queryFn: () => api.get<QontoStatus>('/settings/integrations/qonto/status'), refetchOnWindowFocus: false, retry: 1 };
const rolesQuery = { queryKey: ['roles'] as const, queryFn: () => api.get<Role[]>('/roles') };
const usersAllQuery = { queryKey: ['users', 'stats'] as const, queryFn: () => api.get<{ items: UserItem[] }>('/users?pageSize=200') };
const orgQuery = { queryKey: ['org-settings'] as const, queryFn: () => api.get<OrgSettings>('/settings/org') };

type Voce = 'home' | 'utenti' | 'ruoli' | 'preferenze' | 'integrazioni';

const KIND: Record<string, string> = {
  FATTURA24: 'Fattura24', SLACK: 'Slack', FIGMA: 'Figma', GITHUB: 'GitHub', META_ADS: 'Meta Ads',
  GOOGLE_CALENDAR: 'Google Calendar', ZOOM: 'Zoom', HARVEST: 'Harvest', OTHER: 'Altro',
};
const STATO_INT: Record<string, string> = { ACTIVE: 'collegata', ERROR: 'in errore', DISCONNECTED: 'scollegata' };
const MESI = Array.from({ length: 12 }, (_, i) => new Date(2026, i, 1).toLocaleDateString('it-IT', { month: 'long' }));

const giorno = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' }).replace('.', '') : null);
/** "oggi alle 12:15", "il 6 ott": quando è stato aggiornato un collegamento. */
function quando(iso: string | null | undefined): string {
  if (!iso) return 'mai';
  const d = new Date(iso);
  if (d.toDateString() === new Date().toDateString()) return `oggi alle ${d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`;
  return `il ${giorno(iso)}`;
}

// L'Assistente (28/10/2026): lo stato in una parola, come il chip del computer.
const STATO_AI: Record<StatoAssistente['stato'], string> = {
  attiva: 'acceso',
  finta: 'in prova',
  spenta: 'spento',
  sospesa: 'fermo',
  'senza-chiave': 'manca la chiave',
};
const ICONA_FUNZIONE: Record<string, IconName> = { chat: 'message', dividi: 'list', riscrivi: 'pencil', memoria: 'clock', prova: 'refresh', vocale: 'mic', 'posta-lettura': 'mail', 'posta-invio': 'send' };
const SPIEGA_ACCESO = 'Spento, il gestionale è quello di sempre: niente chat e niente tasti nelle task.';
const SPIEGA_SOLO_LETTURA = 'Nella chat legge e risponde, ma non cambia niente. Dividi e Riscrivi restano: a salvare è sempre la persona.';

/** La riga dell'Assistente nella home: «acceso · 0,42 $ di 8 $ questo mese», «spento», «fermo: …». */
function sottoAssistente(s: StatoAssistente | undefined): string {
  if (!s) return 'controllo…';
  switch (s.stato) {
    case 'attiva': return `acceso · ${dollari(s.spesiUsd)} di ${dollariCorti(s.budgetUsd)} questo mese`;
    case 'finta': return 'in prova: risposte finte';
    case 'spenta': return 'spento: niente chat e niente tasti nelle task';
    case 'sospesa': return `fermo${s.impostazioni.motivoSospensione ? `: ${s.impostazioni.motivoSospensione}` : ''}`;
    default: return 'manca la chiave di Claude sul server';
  }
}

/** Chi può modificare le impostazioni: lo stesso permesso che chiede l'API. */
function useLivelloImpostazioni() {
  const user = useAuthStore((s) => s.user);
  const lvl = user?.permissions.find((p) => p.module === ModuleKey.SETTINGS)?.level ?? PermissionLevel.NONE;
  return { user, scrive: hasAtLeast(lvl, PermissionLevel.WRITE), admin: hasAtLeast(lvl, PermissionLevel.ADMIN) };
}

export function SettingsMobile() {
  const [voceParam, setVoce] = useUrlState<string>('voce', 'home');
  const voce: Voce = (['utenti', 'ruoli', 'preferenze', 'integrazioni'] as string[]).includes(voceParam) ? (voceParam as Voce) : 'home';
  const [utente, setUtente] = useUrlState<string>('utente', '');
  const location = useLocation();
  const navigate = useNavigate();
  // Come il "<" della barra in alto: si torna da dove si è venuti; aperta da
  // un link (niente prima), si torna alla pagina sopra.
  const indietro = (sopra: () => void) => (location.key !== 'default' ? navigate(-1) : sopra());

  // Aprendo una voce si riparte dall'alto.
  useEffect(() => { document.querySelector('.os-main')?.scrollTo({ top: 0 }); }, [voce, utente]);

  let corpo: ReactNode;
  let sopra = 'Impostazioni';
  if (voce === 'utenti' && utente) { corpo = <Utente id={utente} />; sopra = 'Utenti'; }
  else if (voce === 'utenti') corpo = <Utenti onApri={setUtente} />;
  else if (voce === 'ruoli') corpo = <><MHead title="Ruoli: chi vede cosa" /><RuoliSemplici /></>;
  else if (voce === 'preferenze') corpo = <Preferenze />;
  else if (voce === 'integrazioni') corpo = <Integrazioni />;

  if (voce === 'home') return <div className="stm"><Home onVoce={setVoce} /></div>;
  return (
    <div className="stm">
      <MPage>
        {/* Impostazioni è una sezione, non una scheda: la freccia della barra
            in alto qui non c'è, e il ritorno sta in cima alla pagina, come il
            "‹ Impostazioni" dell'iPhone. */}
        <button
          type="button"
          className="stm-indietro"
          onClick={() => indietro(() => (utente ? setUtente('') : setVoce('home')))}
        >
          <Icon name="chevronL" size={16} /> {sopra}
        </button>
        {corpo}
      </MPage>
    </div>
  );
}

// ── La pagina principale ──────────────────────────────────────────────────

function Home({ onVoce }: { onVoce: (v: Voce) => void }) {
  const { data: org } = useQuery(orgQuery);
  const { data: utenti } = useQuery(usersAllQuery);
  const { data: ruoli } = useQuery(rolesQuery);
  const { data: altre } = useQuery({ queryKey: ['integrations'], queryFn: () => api.get<Integration[]>('/settings/integrations') });
  const { data: fic } = useQuery(ficStatusQuery);
  const { data: qonto } = useQuery(qontoStatusQuery);
  // L'Assistente solo agli Amministratori: agli altri /ai/stato risponde 403.
  const amministratore = useAuthStore((s) => s.user?.role.key === 'SUPER_ADMIN');
  const { data: ai } = useQuery({ ...statoAssistenteQuery, enabled: amministratore });

  const tutti = utenti?.items ?? [];
  const attivi = tutti.filter((u) => u.active).length;
  const inErrore = (altre ?? []).filter((i) => i.status === 'ERROR');
  const scollegati = (fic && !fic.connected ? 1 : 0) + (qonto && !qonto.connected ? 1 : 0);
  // L'Assistente fermo da solo conta come un collegamento da sistemare (28/10/2026).
  const problemi = scollegati + inErrore.length + (ai?.stato === 'sospesa' ? 1 : 0);
  const nome = org?.organizationName ?? 'Organizzazione';

  return (
    <MPage>
      {/* L'organizzazione in cima, come l'account in cima alle impostazioni dell'iPhone. */}
      <div className="mk-list">
        <MRow
          text={iniziali(nome)}
          title={nome}
          sub={
            <>
              {tutti.length ? `${tutti.length === 1 ? '1 persona' : `${tutti.length} persone`} · ` : ''}
              {problemi > 0
                ? <span className="is-alert">{problemi === 1 ? '1 collegamento da sistemare' : `${problemi} collegamenti da sistemare`}</span>
                : fic && qonto ? 'collegamenti a posto' : 'controllo i collegamenti…'}
            </>
          }
          onClick={() => onVoce('preferenze')}
        />
      </div>

      <MGroup title="Persone e accessi">
        <MRow icon="users" title="Utenti" sub={tutti.length ? (attivi === tutti.length ? 'tutti attivi' : `${attivi} attivi`) : undefined} trail={tutti.length || undefined} trailTone="mute" onClick={() => onVoce('utenti')} />
        <MRow icon="lock" title="Ruoli" sub="chi vede cosa, in una frase" trail={ruoliVisibili(ruoli).length || undefined} trailTone="mute" onClick={() => onVoce('ruoli')} />
      </MGroup>

      <MGroup title="Servizi collegati">
        <MRow
          icon="invoice"
          tone={fic && !fic.connected ? 'alert' : undefined}
          title="FattureInCloud"
          sub={!fic ? 'controllo…' : fic.connected ? `collegato · aggiornato ${quando(fic.lastSyncAt)}` : 'non collegato: le fatture non arrivano'}
          onClick={() => onVoce('integrazioni')}
        />
        <MRow
          icon="landmark"
          tone={qonto && !qonto.connected ? 'alert' : undefined}
          title="Qonto"
          sub={!qonto ? 'controllo…' : qonto.connected ? `collegato · aggiornato ${quando(qonto.lastSyncAt)}` : 'non collegato: saldi e movimenti non arrivano'}
          onClick={() => onVoce('integrazioni')}
        />
        {amministratore && (
          <MRow
            icon="message"
            tone={ai?.stato === 'sospesa' ? 'alert' : undefined}
            title="MUNE"
            sub={sottoAssistente(ai)}
            onClick={() => onVoce('integrazioni')}
          />
        )}
        {(altre ?? []).map((i) => (
          <MRow
            key={i.id}
            icon="link"
            tone={i.status === 'ERROR' ? 'alert' : undefined}
            title={i.name}
            sub={[STATO_INT[i.status] ?? i.status.toLowerCase(), i.lastSyncAt ? `aggiornata ${quando(i.lastSyncAt)}` : null].filter(Boolean).join(' · ')}
            onClick={() => onVoce('integrazioni')}
          />
        ))}
      </MGroup>

      <MGroup title="Organizzazione">
        <MRow icon="settings" title="Preferenze" sub={org ? `${org.currency} · ${org.timezone} · documenti in ${org.docLanguage === 'it' ? 'italiano' : 'inglese'}` : undefined} onClick={() => onVoce('preferenze')} />
      </MGroup>

      <DesktopOnly
        cosa="Creare un utente o un collegamento nuovo"
        perche="sono moduli da compilare con calma, con chiavi e password da incollare. Dal telefono si consulta tutto e si cambia un valore alla volta."
      ><></></DesktopOnly>
    </MPage>
  );
}

// ── Utenti ────────────────────────────────────────────────────────────────

function Utenti({ onApri }: { onApri: (id: string) => void }) {
  const [search, setSearch] = useState('');
  // Le stesse due chiamate del computer: la lista cercata e quella intera.
  const { data } = useQuery({
    queryKey: ['users', search],
    queryFn: () => api.get<{ items: UserItem[] }>(`/users?pageSize=200${search ? `&search=${encodeURIComponent(search)}` : ''}`),
  });
  const { data: tutti } = useQuery(usersAllQuery);
  const items = data?.items ?? [];
  const totale = tutti?.items.length ?? 0;
  const nAttivi = (tutti?.items ?? []).filter((u) => u.active).length;
  const riga = (u: UserItem) => (
    <MRow
      key={u.id}
      text={u.initials || iniziali(`${u.firstName} ${u.lastName}`)}
      round
      title={`${u.firstName} ${u.lastName}`}
      sub={`${u.role.name} · ${u.email}`}
      trail={u.active ? undefined : 'disattivato'}
      trailTone="mute"
      onClick={() => onApri(u.id)}
    />
  );
  return (
    <>
      <MHead title="Utenti" sub={`${totale === 1 ? '1 persona' : `${totale} persone`} · ${nAttivi === totale ? 'tutte attive' : `${nAttivi} attive`}`} />
      {totale > 6 && <MSearch value={search} onChange={setSearch} placeholder="Cerca un nome o un'email" />}
      <MGroup title="Attivi" count={items.filter((u) => u.active).length || undefined}>
        {items.filter((u) => u.active).map(riga)}
        {!items.some((u) => u.active) && <MEmpty>{search ? `Nessun utente con "${search}".` : 'Nessun utente attivo.'}</MEmpty>}
      </MGroup>
      {items.some((u) => !u.active) && (
        <MGroup title="Disattivati" count={items.filter((u) => !u.active).length}>{items.filter((u) => !u.active).map(riga)}</MGroup>
      )}
    </>
  );
}

type PannelloUtente = null | 'nome' | 'cognome' | 'email' | 'ruolo';

function Utente({ id }: { id: string }) {
  const qc = useQueryClient();
  const { user: io, scrive, admin } = useLivelloImpostazioni();
  const { data: tutti } = useQuery(usersAllQuery);
  const { data: roles } = useQuery(rolesQuery);
  const [aperto, setAperto] = useState<PannelloUtente>(null);
  const [bozza, setBozza] = useState('');
  const u = tutti?.items.find((x) => x.id === id);

  const salva = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.patch(`/users/${id}`, data),
    onSuccess: (_r, data) => {
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['roles'] });
      setAperto(null);
      toast.ok('active' in data ? (data.active ? 'Utente attivato' : 'Utente disattivato') : 'Utente aggiornato');
    },
    onError: (e: any) => toast.alert('Utente non aggiornato', e?.message ?? 'Riprova'),
  });

  if (!tutti) return <MEmpty>Carico l'utente…</MEmpty>;
  if (!u) return <MEmpty>Questo utente non c'è più.</MEmpty>;
  // Il proprio ruolo e il proprio accesso non si toccano da qui: un tocco
  // sbagliato e si resta chiusi fuori, senza un computer per rimediare.
  const sonoIo = io?.id === u.id;
  const apri = (p: PannelloUtente, v: string) => { if (!scrive) return; setBozza(v); setAperto(p); };
  const campo = (tipo: 'text' | 'email') => (
    <input className="inp" type={tipo} autoFocus value={bozza} onChange={(e) => setBozza(e.target.value)} aria-label={aperto ?? ''} />
  );

  return (
    <>
      <MHead title={`${u.firstName} ${u.lastName}`} sub={`${u.role.name} · ${u.active ? 'attivo' : 'disattivato'}${sonoIo ? ' · sei tu' : ''}`} />

      <MGroup title="Anagrafica">
        <MRow title="Nome" trail={u.firstName} trailTone="mute" onClick={scrive ? () => apri('nome', u.firstName) : undefined} />
        <MRow title="Cognome" trail={u.lastName} trailTone="mute" onClick={scrive ? () => apri('cognome', u.lastName) : undefined} />
        <MRow title="Email" sub={u.email} onClick={scrive ? () => apri('email', u.email) : undefined} />
      </MGroup>

      <MGroup title="Accesso" note={sonoIo ? 'È il tuo utente: ruolo e accesso li cambia un altro amministratore.' : undefined}>
        <MRow title="Ruolo" trail={u.role.name} trailTone="mute" onClick={scrive && !sonoIo ? () => apri('ruolo', u.role.id) : undefined} chevron={scrive && !sonoIo} />
        {scrive && !sonoIo && (
          <button
            type="button"
            className={`stm-azione${u.active ? ' is-danger' : ''}`}
            disabled={salva.isPending}
            onClick={async () => {
              if (u.active && !(await confirmDialog({ title: `Disattivare ${u.firstName} ${u.lastName}?`, body: 'Non potrà più entrare nel gestionale finché non lo riattivi.', danger: true, confirmLabel: 'Disattiva' }))) return;
              salva.mutate({ active: !u.active });
            }}
          >
            <Icon name={u.active ? 'lock' : 'check'} size={15} /> {u.active ? "Disattiva l'utente" : "Riattiva l'utente"}
          </button>
        )}
        {admin && !sonoIo && (
          <button
            type="button"
            className="stm-azione"
            onClick={() => useModalStore.getState().open('password', { userId: u.id, userName: `${u.firstName} ${u.lastName}`, mode: 'admin' })}
          >
            <Icon name="refresh" size={15} /> Reimposta la password
          </button>
        )}
      </MGroup>

      <MCampo
        open={aperto === 'nome' || aperto === 'cognome' || aperto === 'email'}
        onClose={() => setAperto(null)}
        titolo={aperto === 'email' ? 'Email' : aperto === 'cognome' ? 'Cognome' : 'Nome'}
        nota={aperto === 'email' ? "È anche l'indirizzo con cui entra nel gestionale." : undefined}
        campo={campo(aperto === 'email' ? 'email' : 'text')}
        onSalva={() => {
          const v = bozza.trim();
          if (!v) return;
          salva.mutate(aperto === 'email' ? { email: v } : aperto === 'cognome' ? { lastName: v } : { firstName: v });
        }}
      />
      <MScelte
        open={aperto === 'ruolo'}
        onClose={() => setAperto(null)}
        titolo="Ruolo"
        nota="Il ruolo decide cosa vede nel gestionale."
        opzioni={[...ruoliAssegnabili(roles), ...(roles ?? []).filter((r) => r.id === u.role.id && ruoloVecchio(r.key))]
          .map((r) => ({ v: r.id, l: ruoloVecchio(r.key) ? `${r.name} (ruolo vecchio)` : r.name, sub: SPIEGA_RUOLO[r.key ?? ''] }))}
        valore={u.role.id}
        onScegli={(v) => { if (v !== u.role.id) salva.mutate({ roleId: v }); else setAperto(null); }}
      />
    </>
  );
}

// ── Preferenze ────────────────────────────────────────────────────────────

type CampoOrg = keyof OrgSettings;

function Preferenze() {
  const qc = useQueryClient();
  const { scrive } = useLivelloImpostazioni();
  const { data: o } = useQuery(orgQuery);
  const [aperto, setAperto] = useState<CampoOrg | null>(null);
  const [bozza, setBozza] = useState('');

  // Lo stesso salvataggio del computer, un campo alla volta.
  const salva = useMutation({
    mutationFn: (data: Partial<OrgSettings>) => api.patch('/settings/org', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-settings'] });
      setAperto(null);
      toast.ok('Preferenza salvata');
    },
    onError: (e: any) => toast.alert('Salvataggio non riuscito', e?.message ?? 'Riprova'),
  });

  if (!o) return <MEmpty>Carico le preferenze…</MEmpty>;
  const apri = (k: CampoOrg, v: string) => { if (!scrive) return; setBozza(v); setAperto(k); };
  const riga = (k: CampoOrg, titolo: string, valore: string, sub?: string) => (
    <MRow title={titolo} sub={sub} trail={valore} trailTone="mute" onClick={scrive ? () => apri(k, String(o[k] ?? '')) : undefined} />
  );
  const scelte = (k: CampoOrg, titolo: string, opzioni: Array<{ v: string; l: string; sub?: string }>, nota?: string) => (
    <MScelte
      open={aperto === k}
      onClose={() => setAperto(null)}
      titolo={titolo}
      nota={nota}
      opzioni={opzioni}
      valore={String(o[k] ?? '')}
      onScegli={(v) => salva.mutate({ [k]: k === 'fiscalYearStart' ? Number(v) : v } as Partial<OrgSettings>)}
    />
  );
  const testo = (k: CampoOrg, titolo: string, input: ReactNode, nota?: string, togli?: boolean) => (
    <MCampo
      open={aperto === k}
      onClose={() => setAperto(null)}
      titolo={titolo}
      nota={nota}
      campo={input}
      onSalva={() => {
        const v = bozza.trim();
        if (k === 'organizationName' && !v) return;
        salva.mutate({ [k]: k === 'logoUrl' ? (v || null) : v } as Partial<OrgSettings>);
      }}
      onTogli={togli && o[k] ? () => salva.mutate({ [k]: null } as Partial<OrgSettings>) : undefined}
      togli="Togli il logo"
    />
  );

  return (
    <>
      <MHead title="Preferenze" sub="Valgono per tutta l'organizzazione e per i documenti." />
      <MGroup title="Identità">
        {riga('organizationName', 'Nome', o.organizationName)}
        {riga('logoUrl', 'Logo', o.logoUrl ? 'impostato' : 'nessuno', o.logoUrl ?? undefined)}
        {riga('primaryColor', 'Colore principale', o.primaryColor)}
      </MGroup>
      <MGroup title="Conti e formati">
        {riga('marginThreshold', 'Soglia del margine', `${Number(o.marginThreshold).toFixed(0)}%`, 'sotto, il progetto va in allerta')}
        {riga('currency', 'Valuta', o.currency)}
        {riga('timezone', 'Fuso orario', o.timezone)}
        {riga('dateFormat', 'Formato delle date', o.dateFormat)}
        {riga('docLanguage', 'Lingua dei documenti', o.docLanguage === 'it' ? 'italiano' : 'inglese')}
        {riga('fiscalYearStart', "Inizio dell'anno fiscale", MESI[Math.max(0, Math.min(11, o.fiscalYearStart - 1))] ?? String(o.fiscalYearStart))}
      </MGroup>

      {testo('organizationName', 'Nome', <input className="inp" autoFocus value={bozza} onChange={(e) => setBozza(e.target.value)} aria-label="Nome dell'organizzazione" />)}
      {testo('logoUrl', 'Logo', <input className="inp" type="url" inputMode="url" autoFocus placeholder="https://…" value={bozza} onChange={(e) => setBozza(e.target.value)} aria-label="Indirizzo del logo" />, "L'indirizzo dell'immagine: finisce sui documenti.", true)}
      {testo('primaryColor', 'Colore principale', <input className="inp mono" autoFocus placeholder="#050519" value={bozza} onChange={(e) => setBozza(e.target.value)} aria-label="Colore principale" />, 'In esadecimale, come #050519.')}
      {testo('marginThreshold', 'Soglia del margine', <input className="inp mono" type="number" inputMode="numeric" min={0} max={100} autoFocus value={bozza} onChange={(e) => setBozza(e.target.value)} aria-label="Soglia del margine in percentuale" />, 'In percentuale: un progetto con un margine più basso va in allerta.')}
      {scelte('currency', 'Valuta', ['EUR', 'USD', 'GBP', 'CHF'].map((v) => ({ v, l: v })))}
      {scelte('timezone', 'Fuso orario', ['Europe/Rome', 'Europe/London', 'Europe/Berlin', 'UTC'].map((v) => ({ v, l: v })))}
      {scelte('dateFormat', 'Formato delle date', [
        { v: 'dd/MM/yyyy', l: 'dd/MM/yyyy', sub: '31/10/2026' },
        { v: 'MM/dd/yyyy', l: 'MM/dd/yyyy', sub: '10/31/2026' },
        { v: 'yyyy-MM-dd', l: 'yyyy-MM-dd', sub: '2026-10-31' },
      ])}
      {scelte('docLanguage', 'Lingua dei documenti', [{ v: 'it', l: 'Italiano' }, { v: 'en', l: 'Inglese' }])}
      {scelte('fiscalYearStart', "Inizio dell'anno fiscale", MESI.map((m, i) => ({ v: String(i + 1), l: m.charAt(0).toUpperCase() + m.slice(1) })))}
    </>
  );
}

// ── Servizi collegati ─────────────────────────────────────────────────────

function Integrazioni() {
  const qc = useQueryClient();
  const { data: fic } = useQuery(ficStatusQuery);
  const { data: qonto } = useQuery(qontoStatusQuery);
  const { data: altre } = useQuery({ queryKey: ['integrations'], queryFn: () => api.get<Integration[]>('/settings/integrations') });
  const [aperta, setAperta] = useState<Integration | null>(null);
  const amministratore = useAuthStore((s) => s.user?.role.key === 'SUPER_ADMIN');

  // Lo stesso aggiornamento della scheda Qonto del computer (e del menu della Situazione).
  const sync = useMutation({
    mutationFn: () => api.post<{ ok?: boolean; stats?: { created?: number; updated?: number } } | undefined>('/admin/sync/qonto'),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['qonto-status'] });
      qc.invalidateQueries({ queryKey: ['admin'] });
      const s = r?.stats;
      toast.ok('Qonto aggiornato', s ? `${s.created ?? 0} movimenti nuovi · ${s.updated ?? 0} aggiornati` : undefined);
    },
    onError: (e: any) => toast.alert('Aggiornamento non riuscito', e?.message ?? 'Riprova'),
  });

  return (
    <>
      <MHead title="Servizi collegati" sub="Da qui arrivano fatture, saldi e movimenti." />

      <MGroup title="FattureInCloud">
        {!fic ? <MEmpty>Controllo il collegamento…</MEmpty> : fic.connected ? (
          <>
            <MRow icon="invoice" title={fic.companyName ?? 'Azienda collegata'} sub={fic.companyId ? `azienda ${fic.companyId}` : undefined} trail="collegato" trailTone="mute" />
            <MRow title="Ultimo aggiornamento" trail={quando(fic.lastSyncAt)} trailTone="mute" />
            <MRow title="Collegato il" trail={giorno(fic.connectedAt) ?? '—'} trailTone="mute" />
          </>
        ) : (
          <MRow icon="invoice" tone="alert" title="Non collegato" sub="senza, fatture e preventivi non arrivano" />
        )}
      </MGroup>

      <MGroup title="Qonto">
        {!qonto ? <MEmpty>Controllo il collegamento…</MEmpty> : qonto.connected ? (
          <>
            <MRow
              icon="landmark"
              title={qonto.accounts?.length ? (qonto.accounts.length === 1 ? '1 conto' : `${qonto.accounts.length} conti`) : 'Conto collegato'}
              sub={qonto.accounts?.length ? qonto.accounts.map((a) => a.name).join(', ') : undefined}
              trail="collegato"
              trailTone="mute"
            />
            <MRow title="Ultimo aggiornamento" trail={quando(qonto.lastSyncAt)} trailTone="mute" />
            <MRow title="Collegato il" trail={giorno(qonto.connectedAt) ?? '—'} trailTone="mute" />
            <button type="button" className="stm-azione" disabled={sync.isPending} onClick={() => sync.mutate()}>
              <Icon name="refresh" size={15} /> {sync.isPending ? 'Aggiorno…' : 'Aggiorna adesso saldi e movimenti'}
            </button>
          </>
        ) : (
          <MRow icon="landmark" tone="alert" title="Non collegato" sub="senza, saldi e movimenti non arrivano in Amministrazione" />
        )}
      </MGroup>

      {amministratore && <GruppoAssistente />}

      <MGroup title="Altri servizi" count={altre?.length || undefined}>
        {(altre ?? []).length ? (altre ?? []).map((i) => (
          <MRow
            key={i.id}
            icon="link"
            tone={i.status === 'ERROR' ? 'alert' : undefined}
            title={i.name}
            sub={[(KIND[i.kind] ?? i.kind) !== i.name ? KIND[i.kind] ?? i.kind : null, STATO_INT[i.status] ?? i.status.toLowerCase()].filter(Boolean).join(' · ')}
            onClick={() => setAperta(i)}
          />
        )) : <MEmpty>Nessun altro servizio collegato.</MEmpty>}
      </MGroup>

      <DesktopOnly
        cosa="Collegare, scollegare o rinnovare un servizio"
        perche="servono chiavi e token da incollare e verificare con calma."
      ><></></DesktopOnly>

      <MobileSheet open={!!aperta} onClose={() => setAperta(null)} title={aperta?.name}>
        {aperta && (
          <div className="mk-list">
            <MRow title="Stato" trail={STATO_INT[aperta.status] ?? aperta.status.toLowerCase()} trailTone={aperta.status === 'ERROR' ? 'alert' : 'mute'} />
            <MRow title="Chiave" trail={aperta.hasKey ? 'configurata' : 'mancante'} trailTone={aperta.hasKey ? 'mute' : 'alert'} />
            {aperta.lastSyncAt && <MRow title="Ultimo aggiornamento" trail={quando(aperta.lastSyncAt)} trailTone="mute" />}
            {aperta.connectedBy && <MRow title="Collegata da" trail={`${aperta.connectedBy.firstName} ${aperta.connectedBy.lastName}`} trailTone="mute" />}
          </div>
        )}
      </MobileSheet>
    </>
  );
}

// ── L'Assistente (solo Amministratori) ────────────────────────────────────

/**
 * La scheda dell'Assistente del computer, a righe (28/10/2026, decisione 15): lo stato,
 * gli interruttori (si toccano e sale la scelta, come le Preferenze), «Riaccendi
 * MUNE» se si è fermato, la spesa del mese per funzione e per persona, e il
 * registro «Cosa ha fatto MUNE» con l'Annulla. Stesse chiamate e stessa cache.
 */
function GruppoAssistente() {
  const qc = useQueryClient();
  const { data: s } = useQuery(statoAssistenteQuery);
  const { data: consumi } = useQuery(consumiQuery());
  const [scelta, setScelta] = useState<null | 'acceso' | 'soloLettura'>(null);

  const salva = useMutation({
    mutationFn: (body: { acceso?: boolean; soloLettura?: boolean }) => salvaImpostazioni(body),
    onSuccess: (nuovo, body) => {
      qc.setQueryData(statoAssistenteQuery.queryKey, nuovo);
      setScelta(null);
      if (body.acceso !== undefined) toast.ok(nuovo.impostazioni.acceso ? 'MUNE acceso' : 'MUNE spento');
      else toast.ok(nuovo.impostazioni.soloLettura ? 'Solo lettura accesa' : 'Solo lettura spenta');
    },
    onError: (e: any) => toast.alert('Impostazione di MUNE non salvata', e?.message ?? 'Riprova'),
  });

  // Riaccendi prova il collegamento, che costa: si rifanno stato e spesa del mese.
  const rinfresca = () => {
    qc.invalidateQueries({ queryKey: statoAssistenteQuery.queryKey });
    qc.invalidateQueries({ queryKey: ['ai', 'consumi'] });
  };
  const riaccendi = useMutation({
    mutationFn: () => api.post<{ testo: string }>('/ai/riaccendi'),
    onSuccess: () => { rinfresca(); toast.ok('MUNE è di nuovo acceso'); },
    onError: (e: any) => { rinfresca(); toast.alert('MUNE è ancora fermo', e?.message ?? 'Riprova'); },
  });

  if (!s) return <MGroup title="MUNE"><MEmpty>Controllo MUNE…</MEmpty></MGroup>;

  const mese = consumi?.mese ?? meseDiAdesso();
  const spesi = consumi?.spesiUsd ?? s.spesiUsd;
  const budget = consumi?.budgetUsd ?? s.budgetUsd;
  const percento = percentoSpeso(spesi, budget);
  const sotto = s.stato === 'sospesa'
    ? (s.impostazioni.motivoSospensione ?? 'si è fermato da solo')
    : s.stato === 'senza-chiave' ? 'manca la chiave di Claude sul server' : 'crediti del piano Max';
  const scegli = (campo: 'acceso' | 'soloLettura', v: string) => {
    const si = v === 'si';
    if (si === s.impostazioni[campo]) { setScelta(null); return; }
    salva.mutate(campo === 'acceso' ? { acceso: si } : { soloLettura: si });
  };

  return (
    <>
      <MGroup title="MUNE">
        <MRow
          icon="message"
          tone={s.stato === 'sospesa' ? 'alert' : undefined}
          title={nomeModello(s.modelloChat ?? s.modello)}
          sub={sotto}
          subWrap={s.stato === 'sospesa'}
          trail={STATO_AI[s.stato]}
          trailTone={s.stato === 'sospesa' ? 'alert' : 'mute'}
        />
        {s.stato !== 'senza-chiave' && (
          <>
            <MRow title="MUNE acceso" trail={s.impostazioni.acceso ? 'sì' : 'no'} trailTone="mute" onClick={() => setScelta('acceso')} />
            <MRow title="Solo lettura" trail={s.impostazioni.soloLettura ? 'sì' : 'no'} trailTone="mute" onClick={() => setScelta('soloLettura')} />
            <MRow title="Memoria di notte" sub="Di notte aggiorna da sola le schede dei clienti." trail="arriva più avanti" trailTone="mute" />
            {/* 2H (29/10/2026): la casella di chi è entrato, collegata per MUNE. */}
            <PostaGoogleMobile />
          </>
        )}
        {s.stato === 'sospesa' && (
          <button type="button" className="stm-azione" disabled={riaccendi.isPending} onClick={() => riaccendi.mutate()}>
            <Icon name="refresh" size={15} /> {riaccendi.isPending ? 'Provo…' : 'Riaccendi MUNE'}
          </button>
        )}
      </MGroup>

      <MGroup title={`Spesa di MUNE a ${meseInParole(mese)}`}>
        <MRow
          title="Speso nel mese"
          sub={(
            <span className="barbar block mt-1.5" role="img" aria-label={`Speso il ${Math.round(percento)} per cento del budget del mese`}>
              <span style={{ width: `${percento}%`, ...(percento >= 80 ? { background: 'var(--alert)' } : {}) }} />
            </span>
          )}
          trail={`${dollari(spesi)} di ${dollariCorti(budget)}`}
        />
        <MRow title="Speso oggi" trail={`${dollari(s.spesiOggiUsd)} di ${dollariCorti(s.budgetGiornoUsd)}`} trailTone="mute" />
      </MGroup>

      {/* Per funzione e per persona in due gruppi, come le due colonne del computer: in un
          elenco solo, dopo le funzioni, le persone sembravano altre funzioni (28/10/2026). */}
      {(consumi?.perFunzione.length ?? 0) > 0 && (
        <MGroup title="Spesa per funzione">
          {consumi!.perFunzione.map((f) => (
            <MRow key={f.codice} icon={ICONA_FUNZIONE[f.codice] ?? 'zap'} title={f.funzione} trail={dollari(f.usd)} trailSub={chiamate(f.chiamate)} />
          ))}
        </MGroup>
      )}
      {(consumi?.perPersona.length ?? 0) > 0 && (
        <MGroup title="Spesa per persona">
          {consumi!.perPersona.map((p) => (
            <MRow key={p.id ?? p.nome} text={iniziali(p.nome)} round title={p.nome} trail={dollari(p.usd)} trailSub={chiamate(p.chiamate)} />
          ))}
        </MGroup>
      )}

      <AzioniAssistenteMobile />

      <MScelte
        open={scelta === 'acceso'}
        onClose={() => setScelta(null)}
        titolo="MUNE acceso"
        nota={SPIEGA_ACCESO}
        opzioni={[{ v: 'si', l: 'Sì' }, { v: 'no', l: 'No' }]}
        valore={s.impostazioni.acceso ? 'si' : 'no'}
        onScegli={(v) => scegli('acceso', v)}
      />
      <MScelte
        open={scelta === 'soloLettura'}
        onClose={() => setScelta(null)}
        titolo="Solo lettura"
        nota={SPIEGA_SOLO_LETTURA}
        opzioni={[{ v: 'si', l: 'Sì' }, { v: 'no', l: 'No' }]}
        valore={s.impostazioni.soloLettura ? 'si' : 'no'}
        onScegli={(v) => scegli('soloLettura', v)}
      />
    </>
  );
}
